import { getPageQuestions } from './questions'
import { getSimpleProperty, getTranslatedProperty } from './properties'
import {
  DEFAULT_BUTTON_SIZE,
  DEFAULT_BUTTON_SHAPE,
  DEFAULT_BUTTON_POSITION,
  DEFAULT_BUTTON_COLOR,
  DEFAULT_BUTTON_FONT_COLOR,
  DEFAULT_FORM_BACKGROUND_COLOR,
  DEFAULT_WALLPAPER_COLOR
} from '../constants/styling'
import {
  FORM_PROPERTY_TYPE_BACKGROUND,
  FORM_PROPERTY_TYPE_BUTTON,
  FORM_PROPERTY_TYPE_DEFAULT_FONT_STYLES,
  FORM_PROPERTY_TYPE_DESCRIPTION,
  FORM_PROPERTY_TYPE_ENDPAGE,
  FORM_PROPERTY_TYPE_ENDPAGE_REDIRECT,
  FORM_PROPERTY_TYPE_ENDPAGE_TEXT,
  FORM_PROPERTY_TYPE_HEADER_LOGO,
  FORM_PROPERTY_TYPE_HIDDEN_FIELDS,
  FORM_PROPERTY_TYPE_TITLE,
  FORM_PROPERTY_TYPE_WALLPAPER
} from '../constants/formPropertyTypes'
import {
  LAUNCH_LEGACY_LOCATION_CREATE,
  LAUNCH_LEGACY_LOCATION_EDIT,
  LAUNCH_LEGACY_LOCATION_FORMNAME
} from '../constants/mixpanel'

const PAGE_NAME = {
  RESPONDENT: 'respondent'
}

const bulkActionsProps = (action, formNum, selectedFormNum, isNewForm) => {
  const props = {
    'Form Type': getFormType(isNewForm),
    '# of Forms Selected': selectedFormNum,
    'All Forms Selected?': selectedFormNum === formNum,
    'Action Performed': action // Delete or assign labels
  }
  return props
}

const getEntryManagerVersion = isNewForm => isNewForm ? 'Forms V2' : 'Classic Forms'

const getFormType = isNewForm => isNewForm ? 'New Forms' : 'Legacy Forms'

export const getActionPerformedEventDetails = (action, { formNum, selectedFormNum, isNewForm }) => {
  return {
    event: 'Form Manager Action Performed',
    properties: bulkActionsProps(action, formNum, selectedFormNum, isNewForm)
  }
}

export const getBulkActionDrawerEventDetails = (action, { formNum, selectedFormNum, isNewForm }) => {
  return {
    event: 'Form Manager Bulk Action Clicked in Drawer',
    properties: bulkActionsProps(action, formNum, selectedFormNum, isNewForm)
  }
}

export const getDuplicateFormEventDetails = (formId, { formNum, isNewForm }) => {
  return {
    event: 'Form Manager Duplicate Form',
    properties: {
      'Form Type View': getFormType(isNewForm),
      'Source Form ID': formId,
      'Total # of Forms': formNum
    }
  }
}

export const getEmbedStylingDiscarded = formId => {
  return {
    event: 'Embedded Styling Changes Discarded',
    properties: {
      'Form ID': formId
    }
  }
}

export const getEmbedStylingLaunched = (formId, { clientUrl, form, formState }) => {

  const questions = getPageQuestions(form, 0)

  const numberOfQuestions = questions?.length || 0
  const questionTypes = [
    ...new Set(
      questions?.map(q => getSimpleProperty(q, 'type'))
    )
  ]

  const {
    fontColour: textColour,
    fontFamily: textFont,
    questionTitleFontSize: titleSize
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_DEFAULT_FONT_STYLES) || {}

  const isDescriptionHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_DESCRIPTION)?.enabled === false
  const isLogoHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_HEADER_LOGO)?.enabled === false
  const isTitleHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_TITLE)?.enabled === false

  const {
    colour: backgroundColor = DEFAULT_FORM_BACKGROUND_COLOR,
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BACKGROUND) || {}

  const {
    backgroundColour = DEFAULT_BUTTON_COLOR,
    colour = DEFAULT_BUTTON_FONT_COLOR,
    position = DEFAULT_BUTTON_POSITION,
    size = DEFAULT_BUTTON_SIZE,
    shape = DEFAULT_BUTTON_SHAPE
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BUTTON) || {}

  return {
    event: 'Embedded Styling Launched',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Embedded Website URL': clientUrl,
      '# of Questions': numberOfQuestions,
      'Question Types Used': questionTypes,
      'Header Logo Visible?': !isLogoHidden,
      'Header Title Visible?': !isTitleHidden,
      'Header Description Visible?': !isDescriptionHidden,
      'Form Background Color': backgroundColor,
      'Form Text Font': textColour,
      'Form Text Color': textFont,
      'Form Question Title Size': titleSize,
      'Submit Button Background Color': backgroundColour,
      'Submit Button Text Color': colour,
      'Submit Button Size': size,
      'Submit Button Shape': shape,
      'Submit Button Position': position
    }
  }
}

export const getEmbedStylingSaved = (formId, { clientUrl, form, formState, wasPublished }) => {

  const questions = getPageQuestions(form, 0)

  const numberOfQuestions = questions?.length || 0
  const questionTypes = [
    ...new Set(
      questions?.map(q => getSimpleProperty(q, 'type'))
    )
  ]

  const {
    fontColour: textColour,
    fontFamily: textFont,
    questionTitleFontSize: titleSize
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_DEFAULT_FONT_STYLES) || {}

  const isDescriptionHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_DESCRIPTION)?.enabled === false
  const isLogoHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_HEADER_LOGO)?.enabled === false
  const isTitleHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_TITLE)?.enabled === false

  const {
    colour: backgroundColor = DEFAULT_FORM_BACKGROUND_COLOR,
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BACKGROUND) || {}

  const {
    backgroundColour = DEFAULT_BUTTON_COLOR,
    colour = DEFAULT_BUTTON_FONT_COLOR,
    position = DEFAULT_BUTTON_POSITION,
    size = DEFAULT_BUTTON_SIZE,
    shape = DEFAULT_BUTTON_SHAPE
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BUTTON) || {}

  return {
    event: 'Embedded Styling Saved',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Embedded Website URL': clientUrl,
      '# of Questions': numberOfQuestions,
      'Question Types Used': questionTypes,
      'Header Logo Visible?': !isLogoHidden,
      'Header Title Visible?': !isTitleHidden,
      'Header Description Visible?': !isDescriptionHidden,
      'Form Background Color': backgroundColor,
      'Form Text Font': textColour,
      'Form Text Color': textFont,
      'Form Question Title Size': titleSize,
      'Submit Button Background Color': backgroundColour,
      'Submit Button Text Color': colour,
      'Submit Button Size': size,
      'Submit Button Shape': shape,
      'Submit Button Position': position,
      'Form Published?': wasPublished
    }
  }
}

export const getEntriesDeletedEventDetails = (formId, { isNewForm, isSelectedAll, numOfEntries, numOfEntriesDeleted }) => {
  return {
    event: 'Entries Deleted',
    properties: {
      'Form ID': formId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      '# of Entries': numOfEntries,
      '# of Entries Deleted': numOfEntriesDeleted,
      'All Entries Selected': isSelectedAll,
    }
  }
}

export const getEntryDeletedEventDetails = (formId, { entryId, isNewForm, numOfEntries }) => {
  return {
    event: 'Entry Deleted',
    properties: {
      'Form ID': formId,
      'Entry ID': entryId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      '# of Entries': numOfEntries
    }
  }
}

export const getEntryDetailsOpenedEventDetails = (formId, { dateCreated, entryId, isNewForm, numOfEntries }) => {
  return {
    event: 'Entry Details Opened',
    properties: {
      'Form ID': formId,
      'Entry Created Date': dateCreated,
      'Entry ID': entryId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      '# of Entries': numOfEntries
    }
  }
}

export const getEntryDetailsNextPrevClicked = (formId, { direction, isNewForm, numOfEntries }) => {
  return {
    event: 'Entry Details Next/Prev Clicked',
    properties: {
      'Form ID': formId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      'Direction': direction,
      '# of Entries': numOfEntries
    }
  }
}

export const getEntryManagerColumnsModified = (formId, { isNewForm, numOfSelectedColumns }) => {
  return {
    event: 'Entry Manager Columns Modified',
    properties: {
      'Form ID': formId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      '# of columns selected': numOfSelectedColumns
    }
  }
}

export const getEntryManagerFilterApplied = (formId, { isFilteredByFormFields, isNewForm, metadataFields,
  numOfEntries, numOfEntriesReturned, numOfFilterArguments, preBuiltFilter }) => {
  return {
    event: 'Entry Manager Filter Applied',
    properties: {
      'Form ID': formId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      'Filtered by Metadata': metadataFields,
      'Is Filtered by Form Fields': isFilteredByFormFields,
      'Pre-built Filter Used': preBuiltFilter, // all, today, expression
      '# of Arguments': numOfFilterArguments,
      '# of Entries': numOfEntries,
      '# of Entries Returned': numOfEntriesReturned
    }
  }
}

export const getEntryManagerFilterCreated = (formId, {
  isFilteredByFormFields, isNewForm, metadataFields, numOfEntries, numOfFilterArguments }) => {
  return {
    event: 'Entry Manager Filter Created',
    properties: {
      'Form ID': formId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      '# of Entries': numOfEntries,
      'Filtered by Metadata': metadataFields,
      'Is Filtered by Form Fields': isFilteredByFormFields,
      '# of Arguments': numOfFilterArguments
    }
  }
}

export const getEntryManagerOpenedEventDetails = (formId, {
  entriesPerPage, filterApplied, isNewForm, numOfEntries, numOfSelectedColumns, sortDirection, sortBy }) => {
  return {
    event: 'Entry Manager Opened',
    properties: {
      'Form ID': formId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      'Entries per page': entriesPerPage,
      'Filter Applied': filterApplied, // all, today, expression
      'Sort by': sortBy,
      'Sort order': sortDirection,
      '# of Entries': numOfEntries,
      '# of columns selected': numOfSelectedColumns
    }
  }
}

export const getEntryPrintedEventDetails = (formId, { entryId, isNewForm, numOfEntries, numOfQuestions }) => {
  return {
    event: 'Entry Printed',
    properties: {
      'Form ID': formId,
      'Entry ID': entryId,
      'Entry Manager Version': getEntryManagerVersion(isNewForm),
      '# of Entries': numOfEntries,
      '# of Questions': numOfQuestions,
    }
  }
}

export const getFBOpenedEventDetails = (formId, { formState, formURL, numOfQuestions, reason }) => {
  return {
    event: 'Form Builder Opened',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Form URL': formURL,
      'Reason': reason, // Scratch, Edit
      '# of Questions': numOfQuestions,
    }
  }
}

export const getFBFormPreviewedEventDetails = (formId, { formState, device }) => {
  return {
    event: 'Form Builder Form Previewed',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Device size': device
    }
  }
}

export const getFBFormPublishedEventDetails = (formId, { form, formState, formURL, language, translate }) => {
  const questions = getPageQuestions(form, 0)
  const numOfHiddenFields = (getSimpleProperty(form, FORM_PROPERTY_TYPE_HIDDEN_FIELDS)?.fields || [])?.length || 0
  const numOfQuestions = questions?.length || 0
  const isCustomLogo = !getSimpleProperty(form, FORM_PROPERTY_TYPE_HEADER_LOGO)?.defaultEnabled

  const questionTypes = [
    ...new Set(
      questions?.map(q => getSimpleProperty(q, 'type'))
    )
  ]

  const {
    colour: backgroundColor = DEFAULT_FORM_BACKGROUND_COLOR,
    imageFileId: bgImageFileId,
    imageUrl: bgImageUrl
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BACKGROUND) || {}
  const isCustomBackgroundColor = backgroundColor !== DEFAULT_FORM_BACKGROUND_COLOR
  const isCustomBackgroundImage = !!bgImageFileId || !!bgImageUrl

  const {
    colour: wallpaperColor = DEFAULT_WALLPAPER_COLOR,
    imageFileId: wpImageFileId,
    imageUrl: wpImageUrl
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_WALLPAPER) || {}
  const isCustomWallpaperColor = wallpaperColor !== DEFAULT_WALLPAPER_COLOR
  const isCustomWallpaperImage = !!wpImageFileId || !!wpImageUrl

  const {
    backgroundColour = DEFAULT_BUTTON_COLOR,
    colour = DEFAULT_BUTTON_FONT_COLOR,
    position = DEFAULT_BUTTON_POSITION,
    size = DEFAULT_BUTTON_SIZE,
    shape = DEFAULT_BUTTON_SHAPE
  } = getSimpleProperty(form, FORM_PROPERTY_TYPE_BUTTON) || {}
  const isCustomSubmitButton = backgroundColour !== DEFAULT_BUTTON_COLOR
    || colour !== DEFAULT_BUTTON_FONT_COLOR
    || position !== DEFAULT_BUTTON_POSITION
    || size !== DEFAULT_BUTTON_SIZE
    || shape !== DEFAULT_BUTTON_SHAPE

  const { mode = FORM_PROPERTY_TYPE_ENDPAGE_TEXT, url } = getSimpleProperty(form, FORM_PROPERTY_TYPE_ENDPAGE) || {}
  const endpageText = getTranslatedProperty(form, 'endpageText', language) || translate('properties:default_form_submission_message')
  const isCustomSubmissionMessage = endpageText !== translate('properties:default_form_submission_message')
  const isSubmissionRedirect = mode === FORM_PROPERTY_TYPE_ENDPAGE_REDIRECT && !!url

  const isDescriptionHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_DESCRIPTION)?.enabled === false
  const isLogoHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_HEADER_LOGO)?.enabled === false
  const isTitleHidden = getSimpleProperty(form, FORM_PROPERTY_TYPE_TITLE)?.enabled === false

  return {
    event: 'Form Builder Form Published',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Form URL': formURL,
      '# of Hidden fields': numOfHiddenFields,
      '# of Questions': numOfQuestions,
      'Question types used': questionTypes,
      'Custom logo?': isCustomLogo,
      'Custom form background color?': isCustomBackgroundColor,
      'custom form background image?': isCustomBackgroundImage,
      'Custom wallpaper color?': isCustomWallpaperColor,
      'Custom wallpaper image?': isCustomWallpaperImage,
      'Custom submit button?': isCustomSubmitButton,
      'Custom thank-you message?': isCustomSubmissionMessage,
      'Description hidden': isDescriptionHidden,
      'Form submission redirect?': isSubmissionRedirect,
      'Logo hidden?': isLogoHidden,
      'Title hidden?': isTitleHidden
    }
  }
}

export const getFBQuestionAddedEventDetails = (formId, { formState, method, numOfQuestions, questionType }) => {
  return {
    event: 'Form Builder Question Added',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      '# of Questions': numOfQuestions,
      'Method Used': method, // Drag and drop, Click
      'Question type': questionType
    }
  }
}

export const getFBQuestionRepositionedEventDetails = (formId, { action, formState, numOfQuestions, questionType }) => {
  return {
    event: 'Form Builder Question Repositioned',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      '# of Questions': numOfQuestions,
      'Action performed': action, // Left-aligned, Right-aligned, Grow width, Shrink width
      'Question type': questionType
    }
  }
}

export const getFBQuestionRemovedEventDetails = (formId, { formState, numOfQuestions, questionType }) => {
  return {
    event: 'Form Builder Question Removed',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Question Type': questionType,
      '# of Questions': numOfQuestions,
    }
  }
}

export const getFBRevertToLastPublished = (formId, { formState }) => {
  return {
    event: 'Form Builder Revert to Last Published',
    properties: {
      'Form ID': formId,
      'Form State': formState
    }
  }
}

export const getFBStateChanged = (formId, { oldState, newState }) => {
  return {
    event: 'Form Builder State Changed',
    properties: {
      'Form ID': formId,
      'Old Form State': oldState,
      'New Form State': newState
    }
  }
}

export const getFBTestResponseSubmitted = (formId, { formState, deviceSize, validationSkipped }) => {
  return {
    event: 'Form Builder Test Response Submitted',
    properties: {
      'Form ID': formId,
      'Form State': formState,
      'Device Size': deviceSize,
      'Validation Skipped?': validationSkipped
    }
  }
}

export const getWelcomePopupSeen = popupName => {
  return {
    event: 'Welcome Notification Displayed',
    properties: {
      'Popup Name': popupName
    }
  }
}

export const getWelcomePopupClosed = (popupName, dismissMethod) => {
  return {
    event: 'Welcome Notification Dismissed',
    properties: {
      'Popup Name': popupName,
      'Method Used to Dismiss': dismissMethod
    }
  }
}

export const getFormManagerLaunchedEventDetails = (forms, onLogin, isNewForm) => {
  const { queryParams, totalCount } = forms
  const { sortKey, sortDirection, filterKey, pageSize, query } = queryParams || {}

  const props = {
    'Sort By': sortKey,
    'Sort Order': sortDirection,
    'Filter Pre-applied?': filterKey !== 'all',
    'Form Type View': getFormType(isNewForm),
    'Search Pre-applied?': query ? true : false,
    'Forms Per Page': pageSize,
    'Total # of Forms': totalCount,
    'On Login?': onLogin
  }
  return { event: 'Form Manager Launched', properties: props }
}

export const getFormRenameEventDetails = (formId, isNewForm) => {
  return {
    event: 'Form Manager Form Rename',
    properties: {
      'Form ID': formId,
      'Form Type': getFormType(isNewForm)
    }
  }
}

export const getLaunchFBEventDetails = (location, { form, createFormType, isNewForm } = {}) => {
  let purpose

  switch (location) {
    case LAUNCH_LEGACY_LOCATION_CREATE:
      purpose = 'Create New'
      break
    case LAUNCH_LEGACY_LOCATION_EDIT:
    case LAUNCH_LEGACY_LOCATION_FORMNAME:
      purpose = 'Edit'
      break
    default:
      purpose = ''
      break
  }

  const props = {
    'Action Invoked Location': location,
    'Purpose': purpose,
    'Form ID': form?.id || '',
    'Form Type': isNewForm ? 'New Form Builder' : 'Legacy Form Builder',
    'Password Required?': form?.password ? true : false,
    'Create Form': createFormType || ''
  }

  return { event: 'Form Builder Launched', properties: props }
}

export const getQueryParamsEventDetails = (queryDiff, queryObj, data) => {

  const { query, labelIds, filterKey, sortKey, sortDirection, page, pageSize } = queryObj
  const { countLimit, isNewForm, totalCount } = data

  // for the case where on page 2+ and changing search, filters, or sort, we shouldn't count the page change to 1 a page turned event
  const queryDiffKeys = Object.keys(queryDiff)
  if (queryDiffKeys.length > 1 && queryDiffKeys.includes('page')) delete queryDiff.page

  let event
  let props
  let mixpanelData = []
  const keys = Object.keys(queryDiff)
  keys.forEach(key => {
    const hasFilter = labelIds.length > 0 || filterKey !== 'all'
    switch (key) {
      case 'query':
        event = 'Form Manager Search'
        props = {
          'Search Type': query ? 'Terms' : 'Reset',
          'Form Type': getFormType(isNewForm),
          'Forms Per Page': pageSize,
          '# of Forms Returned': totalCount,
          'Has Filter?': hasFilter
        }
        break
      case 'labelIds':
        event = 'Form Manager Filter by Labels'
        props = {
          '# of Labels Applied': labelIds.length,
          '# of Forms Returned': totalCount,
          'Form Type': getFormType(isNewForm),
          'Search Applied?': query ? true : false,
          'Visibility': filterKey,
        }
        break
      case 'sortKey':
      case 'sortDirection':
        event = 'Form Manager Sort'
        props = {
          'Form Type': getFormType(isNewForm),
          'Forms Per Page': pageSize,
          'Total # of Forms': totalCount,
          'Old Sort By': key === 'sortKey' ? queryDiff.sortKey : sortKey,
          'New Sort By': sortKey,
          'Old Sort Order': key === 'sortDirection' ? queryDiff.sortDirection : sortDirection,
          'New Sort Order': sortDirection
        }
        break
      case 'page':
        event = 'Form Manager Page Turned'
        props = {
          'Page Turned Type': queryDiff.page < page ? 'Next' : 'Previous',
          'Form Type': getFormType(isNewForm),
          'Forms Per Page': pageSize,
          '# of Forms Returned': totalCount,
          'Has Filter?': hasFilter,
          '# of Pages': totalCount%pageSize === 0 ? totalCount/pageSize : Math.trunc((totalCount/pageSize) + 1)
        }
        break
      default:
        break
    }
    if (event) mixpanelData.push({ 'event': event, 'properties': { ...props, 'Total # of Forms in Account': countLimit }})
  })
  return mixpanelData
}

export const getRespondentFormRenderedEventDetails = (formId, { embedHostUrl, formAlias, formState, formUrl,
  hasHiddenFields = false, isEmbed = false, profileUuid, viewportHeight , viewportWidth }) => {
  return {
    eventName: 'form_rendered',
    eventType: 'form_rendered',
    params: {
      formId,
      formState,
      formUrl,
      customUrlDefined: (!!formAlias).toString(),
      embedded: isEmbed.toString(),
      ...(isEmbed ? { embeddedWebsiteUrl: embedHostUrl } : {}),
      hiddenFieldsSpecified: hasHiddenFields.toString(),
      profileUuid,
    },
    webContext: {
      pageName: PAGE_NAME.RESPONDENT,
      viewportHeight,
      viewportWidth,
    },
  }
}

export const getRespondentFormSubmittedEventDetails = (formId, { embedHostUrl, formAlias, formState, formUrl,
  hasHiddenFields = false, isEmbed = false, isRedirect = false, profileUuid, viewportHeight, viewportWidth }) => {
  return {
    eventName: 'form_submitted',
    eventType: 'form_submitted',
    params: {
      formId,
      formState,
      formUrl,
      customUrlDefined: (!!formAlias).toString(),
      embedded: isEmbed.toString(),
      ...(isEmbed ? { embeddedWebsiteUrl: embedHostUrl } : {}),
      hiddenFieldsSpecified: hasHiddenFields.toString(),
      profileUuid,
      redirected: isRedirect.toString(),
    },
    webContext: {
      pageName: PAGE_NAME.RESPONDENT,
      viewportHeight,
      viewportWidth,
    },
  }
}

export const getUpgradeCTAClickedEventDetails = (upgradeTextKey, location) => {
  return {
    event: 'Upgrade CTA Clicked',
    properties: {
      'CTA Name': upgradeTextKey,
      'Action Invoked Location': location
    }
  }
}

export const getViewFormLaunchedEventDetails = (formId, isNewForm) => {
  return {
    event: 'Form Manager View Form',
    properties: {
      'Form ID': formId,
      'Form Type': getFormType(isNewForm)
    }
  }
}
